import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Search, X, Gem } from 'lucide-react';
import { distanceKmFromUser } from './distance';

// Only surface tag suggestions from venues/experiences within this radius
// of the guest. Matches the map's NEARBY_RADIUS_KM so search never lies
// about what's actually available around them.
const SEARCH_RADIUS_KM = 20;
const MAX_SUGGESTIONS = 5;
const MAX_TAGS = 3;

/**
 * Guest map search bar — MULTI-TAG autocomplete with mixed venue +
 * experience ("Hidden Gem") suggestions.
 *
 * - Suggestions are limited to tags actually present on at least one
 *   venue OR experience within 20 km of the guest — no ghost tags.
 * - Venue suggestions are prefixed with a 🔎 magnifying-glass icon;
 *   experience suggestions with a 💎 gem so guests can tell them apart
 *   at a glance ("souvlaki" vs "hiking").
 * - A small gem toggle sits inside the search bar on the right. When ON
 *   (default, shimmering), both venue + experience tags surface. When
 *   OFF, only venue tags appear in suggestions.
 * - Selected tags render as pills beneath the input with kind-specific
 *   icons. The 3-tag cap is SHARED across venue + experience tags.
 * - Adding a venue tag → filters venue pins; adding an experience tag →
 *   filters experience pins. Both filters run independently so a guest
 *   can combine "burger" + "views-scenic" and see both layers.
 */
export function GuestMapSearchBar({
  inputValue,
  onInputChange,
  selectedVenueTags = [],
  selectedExperienceTags = [],
  onAddVenueTag,
  onAddExperienceTag,
  onRemoveVenueTag,
  onRemoveExperienceTag,
  venues = [],
  experiences = [],
  userPosition = null,
  experienceTagOn = true,
  onToggleExperienceTag,
  onFocus,
  onBlur,
}) {
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Tags present on venues within 20 km. When we have no user position
  // yet we fall back to every loaded venue so search stays useful before
  // geolocation resolves.
  const availableVenueTags = useMemo(() => {
    const set = new Set();
    (venues || []).forEach((v) => {
      if (userPosition) {
        const d = distanceKmFromUser(v, userPosition);
        if (!Number.isFinite(d) || d > SEARCH_RADIUS_KM) return;
      }
      const tags = Array.isArray(v?.tags) ? v.tags : [];
      tags.forEach((tag) => {
        if (tag && typeof tag === 'string') {
          const clean = tag.trim();
          if (clean) set.add(clean.toLowerCase());
        }
      });
    });
    return Array.from(set).sort();
  }, [venues, userPosition]);

  // Tags present on approved experiences within 20 km. Suppressed when
  // the gem toggle is OFF so the guest never sees "hiking" mixed in with
  // "souvlaki" against their preference.
  const availableExperienceTags = useMemo(() => {
    if (!experienceTagOn) return [];
    const set = new Set();
    (experiences || []).forEach((e) => {
      if (userPosition) {
        const d = distanceKmFromUser(
          { latitude: e.latitude, longitude: e.longitude },
          userPosition,
        );
        if (!Number.isFinite(d) || d > SEARCH_RADIUS_KM) return;
      }
      const tags = Array.isArray(e?.tags) ? e.tags : [];
      tags.forEach((tag) => {
        if (tag && typeof tag === 'string') {
          const clean = tag.trim();
          if (clean) set.add(clean.toLowerCase());
        }
      });
    });
    return Array.from(set).sort();
  }, [experiences, userPosition, experienceTagOn]);

  const selectedVenueSet = useMemo(
    () => new Set(selectedVenueTags.map((t) => (t || '').toLowerCase())),
    [selectedVenueTags],
  );
  const selectedExperienceSet = useMemo(
    () => new Set(selectedExperienceTags.map((t) => (t || '').toLowerCase())),
    [selectedExperienceTags],
  );

  // Combined suggestion list — venue matches first (guests are usually
  // hungry before they're curious), then experience matches. Both are
  // filtered by the typed query with startsWith preferred over contains
  // so "sou" surfaces "souvlaki" ahead of "sourdough".
  const suggestions = useMemo(() => {
    const q = (inputValue || '').trim().toLowerCase();
    if (!q) return [];
    const venuePool = availableVenueTags
      .filter((t) => !selectedVenueSet.has(t))
      .map((t) => ({ tag: t, kind: 'venue' }));
    const expPool = availableExperienceTags
      .filter((t) => !selectedExperienceSet.has(t))
      .map((t) => ({ tag: t, kind: 'experience' }));
    const pool = [...venuePool, ...expPool];
    const starts = pool.filter(({ tag }) => tag.startsWith(q));
    const contains = pool.filter(({ tag }) => !tag.startsWith(q) && tag.includes(q));
    return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [inputValue, availableVenueTags, availableExperienceTags, selectedVenueSet, selectedExperienceSet]);

  // Dismiss the dropdown on outside tap.
  useEffect(() => {
    if (!focused) return undefined;
    const onDocClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [focused]);

  const commitSuggestion = ({ tag, kind }) => {
    // Blur first; map expansion follows the physical viewport restoration.
    inputRef.current?.blur();
    setFocused(false);
    if (kind === 'venue' && typeof onAddVenueTag === 'function') onAddVenueTag(tag);
    if (kind === 'experience' && typeof onAddExperienceTag === 'function') onAddExperienceTag(tag);
  };

  const totalSelected = selectedVenueTags.length + selectedExperienceTags.length;
  const atMax = totalSelected >= MAX_TAGS;
  const showDropdown = focused && suggestions.length > 0;

  const allSelected = useMemo(() => ([
    ...selectedVenueTags.map((t) => ({ tag: t, kind: 'venue' })),
    ...selectedExperienceTags.map((t) => ({ tag: t, kind: 'experience' })),
  ]), [selectedVenueTags, selectedExperienceTags]);

  return (
    <div ref={containerRef} style={{ padding: '4px 16px 8px', flexShrink: 0, position: 'relative' }}>
      <style>{`
        @keyframes allureGemShimmer {
          0%   { transform: scale(1) rotate(0deg); }
          40%  { transform: scale(1.14) rotate(6deg); }
          80%  { transform: scale(0.98) rotate(-3deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes allureGemHalo {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.18); }
        }
        @keyframes allureGemSweep {
          0%   { transform: translateX(-140%) rotate(20deg); opacity: 0; }
          25%  { opacity: 0.6; }
          60%  { transform: translateX(140%) rotate(20deg); opacity: 0; }
          100% { transform: translateX(140%) rotate(20deg); opacity: 0; }
        }
        .allure-gem-icon-on { animation: allureGemShimmer 2.6s ease-in-out infinite; transform-origin: center; }
        .allure-gem-halo    { animation: allureGemHalo 2.6s ease-in-out infinite; transform-origin: center; }
        .allure-gem-sweep   { animation: allureGemSweep 3s ease-in-out infinite; }
      `}</style>

      <div className="premium-search-shell" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        backgroundColor: 'var(--surface)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid var(--card-border)',
        borderRadius: 18, padding: '12px 14px',
        boxShadow: '0 8px 20px rgba(112, 63, 246, 0.06)',
        opacity: atMax ? 0.72 : 1,
      }}>
        <Search size={17} color="#7E76A1" />
        <input
          ref={inputRef}
          type="text"
          placeholder={atMax ? 'Remove a tag to search another' : 'Search a tag (e.g. sushi, hiking)…'}
          value={inputValue || ''}
          onChange={(e) => { onInputChange(e.target.value); if (!focused) setFocused(true); }}
          onFocus={() => { setFocused(true); if (typeof onFocus === 'function') onFocus(); }}
          onBlur={() => { if (typeof onBlur === 'function') onBlur(); }}
          data-testid="map-search"
          autoCapitalize="none"
          autoCorrect="on"
          spellCheck
          disabled={atMax}
          style={{ flex: 1, minWidth: 0, border: 'none', background: 'none', outline: 'none', fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text)' }}
        />

        {inputValue ? (
          <button
            type="button"
            onClick={() => onInputChange('')}
            data-testid="map-search-clear"
            aria-label="Clear search text"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: 999, border: 'none',
              background: 'var(--accent-surface-soft)', color: 'var(--text-2)',
              cursor: 'pointer', padding: 0, flexShrink: 0,
            }}
          >
            <X size={13} strokeWidth={2.4} />
          </button>
        ) : null}

        {/* Gem toggle — enables/disables experience tag suggestions.
            Shimmers gently while ON so guests notice it, and has a
            radial halo + sweeping shine that mimic a real gemstone
            catching the light. */}
        <button
          type="button"
          onClick={() => onToggleExperienceTag && onToggleExperienceTag()}
          data-testid="map-search-experiences-toggle"
          aria-pressed={experienceTagOn}
          aria-label={experienceTagOn ? 'Hide experience suggestions' : 'Show experience suggestions'}
          style={{
            position: 'relative',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 999,
            border: '1px solid ' + (experienceTagOn ? 'transparent' : 'var(--card-border)'),
            background: experienceTagOn
              ? 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)'
              : 'var(--surface)',
            color: experienceTagOn ? '#FFFFFF' : '#7C3AED',
            cursor: 'pointer', padding: 0, flexShrink: 0,
            boxShadow: experienceTagOn
              ? '0 6px 14px rgba(124, 58, 237, 0.38)'
              : 'none',
            transition: 'background 240ms ease, box-shadow 240ms ease, border-color 200ms ease',
            overflow: 'hidden',
          }}
        >
          {experienceTagOn ? (
            <>
              <span
                aria-hidden
                className="allure-gem-halo"
                style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: 999,
                  background: 'radial-gradient(circle at center, rgba(178,138,255,0.75), rgba(178,138,255,0) 65%)',
                  pointerEvents: 'none',
                }}
              />
              <span
                aria-hidden
                className="allure-gem-sweep"
                style={{
                  position: 'absolute',
                  top: 0, bottom: 0, left: 0,
                  width: '60%',
                  background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent)',
                  pointerEvents: 'none',
                }}
              />
            </>
          ) : null}
          <Gem
            size={16}
            strokeWidth={2.4}
            className={experienceTagOn ? 'allure-gem-icon-on' : undefined}
            style={{ position: 'relative', zIndex: 1 }}
          />
        </button>
      </div>

      {/* Selected pills — venue + experience share the row so the guest
          can see all their active filters at a glance. */}
      {allSelected.length > 0 ? (
        <div
          data-testid="map-search-selected-tags"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 6,
            padding: '8px 2px 2px',
          }}
        >
          {allSelected.map(({ tag, kind }) => (
            <span
              key={`${kind}-${tag}`}
              data-testid={`map-search-selected-tag-${kind}-${tag.replace(/[^a-z0-9]+/g, '-')}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 6px 5px 10px',
                borderRadius: 999,
                background: kind === 'experience'
                  ? 'linear-gradient(135deg, rgba(124,58,237,0.14), rgba(79,70,229,0.14))'
                  : 'var(--accent-surface-soft)',
                border: '1px solid ' + (kind === 'experience' ? 'rgba(124,58,237,0.34)' : 'var(--card-border)'),
                color: 'var(--text)',
                fontFamily: "'Inria Serif', serif",
                fontSize: 12.5, fontWeight: 700,
                letterSpacing: '0.01em',
                maxWidth: '100%',
              }}
            >
              {kind === 'experience'
                ? <Gem size={12} strokeWidth={2.4} color="#7C3AED" />
                : <Search size={11} strokeWidth={2.4} color="#7E76A1" />}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{tag}</span>
              <button
                type="button"
                onClick={() => {
                  if (kind === 'venue' && onRemoveVenueTag) onRemoveVenueTag(tag);
                  if (kind === 'experience' && onRemoveExperienceTag) onRemoveExperienceTag(tag);
                }}
                aria-label={`Remove ${tag}`}
                data-testid={`map-search-selected-tag-remove-${kind}-${tag.replace(/[^a-z0-9]+/g, '-')}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 999, border: 'none',
                  background: 'var(--surface)',
                  color: 'var(--text-2)',
                  cursor: 'pointer', padding: 0,
                  boxShadow: '0 1px 2px rgba(20, 12, 44, 0.08)',
                }}
              >
                <X size={11} strokeWidth={2.6} />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {showDropdown ? (
        <div
          data-testid="map-search-suggestions"
          style={{
            position: 'absolute',
            top: 'calc(100% - 2px)',
            left: 16,
            right: 16,
            marginTop: 6,
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 16,
            boxShadow: '0 14px 36px rgba(20, 12, 44, 0.14)',
            overflow: 'hidden',
            zIndex: 40,
          }}
        >
          {suggestions.map(({ tag, kind }, idx) => (
            <button
              key={`${kind}-${tag}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commitSuggestion({ tag, kind })}
              data-testid={`map-search-suggestion-${kind}-${tag.replace(/[^a-z0-9]+/g, '-')}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '11px 14px',
                background: 'none', border: 'none',
                borderTop: idx === 0 ? 'none' : '1px solid var(--hairline)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: "'Inria Serif', serif",
                fontSize: 14.5, fontWeight: 600, color: 'var(--text)',
              }}
            >
              {kind === 'experience'
                ? <Gem size={14} color="#7C3AED" strokeWidth={2.4} />
                : <Search size={13} color="var(--accent-purple)" strokeWidth={2.4} />}
              <span style={{ flex: 1 }}>{tag}</span>
              {kind === 'experience' ? (
                <span style={{
                  fontSize: 10, fontWeight: 800,
                  color: '#7C3AED', letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  Gem
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
